import instance from "./axiosInstance";

export const renameMyDevice = async (deviceId, machineName) =>
  (
    await instance.put(
      `/deviceUser/name/${encodeURIComponent(deviceId)}`,
      { machineName },
      { headers: { "Content-Type": "application/json" } }
    )
  ).data;
export const registerMachineCode = async (deviceId, machineCode) =>
  (
    await instance.put(
      `/device/${encodeURIComponent(deviceId)}/machine-code`,
      { machineCode },
      { headers: { "Content-Type": "application/json" } }
    )
  ).data;
export const bindMyDevice = async (machineCode) =>
  (await instance.post(`/deviceUser/save/${encodeURIComponent(machineCode)}`))
    .data;
export const deviceIdentitySettings = async () =>
  (await instance.get("/device/identity-settings")).data;
export const unbindMyDevice = async (deviceId) =>
  (await instance.delete(`/deviceUser/remove/${encodeURIComponent(deviceId)}`))
    .data;

/**
 * 添加设备
 * @param {Object} device 设备信息
 */
export const addDevice = async (device) => {
  try {
    const response = await instance({
      method: "POST",
      url: "/device",
      headers: {
        "Content-Type": "application/json",
      },
      data: device,
    });
    return response.data;
  } catch (error) {
    console.error("添加设备异常:", error);
    throw error;
  }
};

/**
 * 根据设备ID删除设备
 * @param {String} deviceId 设备ID
 */
export const removeDevice = async (deviceId) => {
  try {
    const response = await instance({
      method: "DELETE",
      url: `/device/remove/${deviceId}`,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    console.error("删除设备异常:", error);
    throw error;
  }
};

/**
 * 查询所有设备（管理员可用）
 */
export const listAllDevices = async () => {
  try {
    const response = await instance({
      method: "GET",
      url: "/device/list",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    console.error("查询所有设备异常:", error);
    // API调用失败时返回空数组，确保后续代码能继续执行
    return [];
  }
};

/**
 * 根据当前登录用户ID查询设备
 */
export const listDevicesByUserId = async () => {
  try {
    const response = await instance({
      method: "GET",
      url: "/device/listByUserId",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    console.error("查询用户设备异常:", error);
    // API调用失败时返回空数组，确保后续代码能继续执行
    return [];
  }
};

/**
 * 根据设备ID获取设备信息
 * @param {String} deviceId 设备ID
 */
export const getDeviceInfo = async (deviceId) => {
  try {
    const response = await instance({
      method: "GET",
      url: `/device/get/${deviceId}`,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    console.error("获取设备信息异常:", error);
    throw error;
  }
};

/**
 * 批量添加设备与用户的关系
 * @param {Object} data { deviceIds: string[], phone: string }
 */
export const batchBindDeviceUser = async (data) => {
  try {
    const response = await instance({
      method: "POST",
      url: "/deviceUser/save",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    });
    return response.data;
  } catch (error) {
    console.error("批量绑定设备用户异常:", error);
    throw error;
  }
};

/**
 * 批量检测设备是否在线
 * @param {Array} deviceIds 设备ID数组
 */
export const checkDevicesOnline = async (deviceIds) => {
  try {
    const response = await instance({
      method: "POST",
      url: "/control/online/batch",
      headers: {
        "Content-Type": "application/json",
      },
      data: deviceIds,
    });
    return response.data;
  } catch (error) {
    console.error("批量检测设备在线状态异常:", error);
    // API调用失败时返回空对象，确保后续代码能继续执行
    return {};
  }
};

/**
 * 批量获取设备GPS坐标
 * @param {Array} deviceIds 设备ID数组
 */
export const batchGetDevicesLocation = async (deviceIds) => {
  try {
    const response = await instance({
      method: "POST",
      url: "/control/location/batch",
      headers: {
        "Content-Type": "application/json",
      },
      data: deviceIds,
    });
    return response.data;
  } catch (error) {
    console.error("批量获取设备GPS坐标异常:", error);
    throw error;
  }
};

/**
 * 获取设备定时拍照配置
 * @param {String} deviceId 设备ID
 */
export const getCaptureSchedule = async (deviceId) => {
  try {
    const response = await instance({
      method: "GET",
      url: `/device/capture-schedule/${deviceId}`,
    });
    return response.data;
  } catch (error) {
    console.error("获取定时拍照配置异常:", error);
    throw error;
  }
};

/**
 * 保存设备定时拍照配置
 * @param {Object} data { deviceId, enabled, times }
 */
export const saveCaptureSchedule = async (data) => {
  try {
    const response = await instance({
      method: "POST",
      url: "/device/capture-schedule",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    });
    return response.data;
  } catch (error) {
    console.error("保存定时拍照配置异常:", error);
    throw error;
  }
};

/**
 * 获取设备定时录制视频配置
 * @param {String} deviceId 设备ID
 */
export const getVideoSchedule = async (deviceId) => {
  try {
    const response = await instance({
      method: "GET",
      url: "/device/video-schedule/" + deviceId,
    });
    return response.data;
  } catch (error) {
    console.error("获取定时录制视频配置异常:", error);
    throw error;
  }
};

/**
 * 保存设备定时录制视频配置
 * @param {Object} data { deviceId, enabled, times }
 */
export const saveVideoSchedule = async (data) => {
  try {
    const response = await instance({
      method: "POST",
      url: "/device/video-schedule",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    });
    return response.data;
  } catch (error) {
    console.error("保存定时录制视频配置异常:", error);
    throw error;
  }
};
